import { memo, useEffect } from 'react';
import { DARK, Flavor, LIGHT, layers } from '@protomaps/basemaps';
import {
  BackgroundLayerSpecification,
  FillLayerSpecification,
  LayerSpecification,
  GlobeControl as MaplibreGlobeControl,
  Map as MaplibreMap,
  setWorkerUrl,
} from 'maplibre-gl';
import { Cache, DecompressFunc, Header, PMTiles, RangeResponse, Source } from 'pmtiles';
import { ControlPosition, IControl, useControl } from 'react-map-gl/maplibre';
import { getWorkerUrl } from './maplibre-worker';
import './maplibre.css';

setWorkerUrl(getWorkerUrl());

export interface ControlOptions {
  position?: ControlPosition;
}

interface DeviceOrientationEventMultiPlatform extends DeviceOrientationEvent {
  requestPermission?: () => Promise<'granted' | 'denied'>; // iOS only
  webkitCompassHeading?: number; // iOS only
}

const create: <K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className?: string,
  container?: HTMLElement
) => HTMLElementTagNameMap[K] = (tagName, className, container) => {
  const el = window.document.createElement(tagName);
  if (className !== undefined) {
    el.className = className;
  }
  if (container) {
    container.appendChild(el);
  }
  return el;
};

class SourceSwitcher implements IControl {
  container = create('div', 'maplibregl-ctrl maplibregl-ctrl-source closed');
  sources: string[];
  constructor(sources: string[], direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse') {
    this.sources = sources;
    this.container.style.flexDirection = direction || 'row';
    this.container.addEventListener('click', (event: MouseEvent) => {
      if (this.container.classList.contains('closed')) {
        this.container.classList.remove('closed');
        event.preventDefault();
      } else {
        this.container.classList.add('closed');
      }
    });
  }

  onAdd(map: MaplibreMap) {
    //setTimeout(() => {
    this.container.replaceChildren();
    Object.values(map.style._layers || [])
      .map((s) => s.source)
      .filter((value, index, array) => value && array.indexOf(value) === index)
      .filter((source) => this.sources.indexOf(source) > -1)
      .forEach((source) => {
        const active = Object.values(map.style._layers).some(
          (layer) => layer.source === source && layer.visibility !== 'none'
        );
        const sourceIcon = create(
          'img',
          `${active ? 'active ' : ''}source-icon source-icon-${source}`,
          this.container
        );
        sourceIcon.addEventListener('click', () => {
          const activeIcon = this.container.querySelector('.active');
          activeIcon?.classList.remove('active');
          sourceIcon.classList.add('active');
          if (sourceIcon !== activeIcon && !this.container.classList.contains('closed')) {
            if (source) {
              Object.values(map.style._layers)
                .filter((layer) => !layer.source || this.sources.indexOf(layer.source) > -1)
                .forEach((layer) =>
                  map.setLayoutProperty(
                    layer.id,
                    'visibility',
                    layer.source === source ? 'visible' : 'none'
                  )
                );
            }
          }
        });
      });
    //});
    return this.container;
  }

  onRemove() {
    if (this.container) {
      this.container.remove();
    }
  }
}

// Inspiration: https://stackoverflow.com/questions/42863340/how-to-stop-mapbox-gl-js-tracking-my-mobile-compass
class Compass implements IControl {
  _container = create('div', 'maplibregl-ctrl maplibregl-ctrl-group');

  onAdd(map: MaplibreMap & { touching: boolean }): HTMLElement {
    const orientationListener = (event: DeviceOrientationEventMultiPlatform) => {
      if (!map.touching) {
        map.easeTo({
          bearing:
            event.webkitCompassHeading !== undefined
              ? event.webkitCompassHeading
              : event.absolute === true && event.alpha
                ? event.alpha * -1
                : 0,
          animate: false,
        });
      }
    };
    const button = create('button', 'maplibregl-ctrl-compass-off', this._container);
    create('span', 'maplibregl-ctrl-icon', button).setAttribute('aria-hidden', 'true');
    button.type = 'button';
    let enabled = false;
    button.addEventListener('click', async () => {
      const eventType =
        'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
      const requestPermission =
        DeviceOrientationEvent &&
        (DeviceOrientationEvent as unknown as DeviceOrientationEventMultiPlatform)
          .requestPermission;
      if (typeof requestPermission === 'function' && !enabled) {
        enabled = (await requestPermission()) === 'granted';
      } else {
        enabled = !enabled;
      }
      button.className = `maplibregl-ctrl-compass-${enabled ? 'on' : 'off'}`;
      if (enabled) {
        window.addEventListener(eventType, orientationListener);
      } else {
        window.removeEventListener(eventType, orientationListener);
        map.easeTo({
          bearing: 0,
          animate: true,
        });
      }
    });
    map.on('touchstart', () => {
      map.touching = true;
    });
    map.on('touchend', () => {
      map.touching = false;
    });
    map.on('touchcancel', () => {
      map.touching = false;
    });
    return this._container;
  }

  onRemove() {
    if (this._container) {
      this._container.remove();
    }
  }
}

class Toggle implements IControl {
  container: HTMLDivElement | undefined;
  button: HTMLButtonElement | undefined;
  toggle: () => void;

  constructor(toggle: () => void) {
    this.toggle = toggle;
  }

  onAdd(_map: MaplibreMap): HTMLElement {
    this.container = create('div', 'maplibregl-ctrl maplibregl-ctrl-group');
    this.button = create('button', undefined, this.container);
    create('span', 'maplibregl-ctrl-icon', this.button).setAttribute('aria-hidden', 'true');
    this.button!.type = 'button';
    this.button!.addEventListener('click', this.toggle);
    return this.container!;
  }

  setClassName(className: string) {
    if (this.button) {
      this.button.className = className;
    }
  }

  onRemove() {
    if (this.container) {
      this.container.remove();
    }
  }
}

export const GlobeControl = memo((props: ControlOptions) => {
  useControl(() => new MaplibreGlobeControl(), props);
  return null;
});
export const CompassControl = memo((props: ControlOptions) => {
  useControl(() => new Compass(), props);
  return null;
});
export const SourceControl = memo(
  ({
    sources,
    direction,
    ...props
  }: {
    sources: string[];
    direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  } & ControlOptions) => {
    useControl(() => new SourceSwitcher(sources, direction), props);
    return null;
  }
);
export const ToggleControl = memo(
  ({
    className,
    toggle,
    ...props
  }: ControlOptions & {
    className: string;
    toggle: () => void;
  }) => {
    const ctrl = useControl(() => new Toggle(toggle), props);
    useEffect(() => {
      ctrl.setClassName(className);
    }, [className]);
    return null;
  }
);

export class FallbackPMTiles extends PMTiles {
  fallbackPMTiles: PMTiles;
  originalBounds: [number, number, number, number] | undefined;
  constructor(
    source: Source | string,
    fallbackSource: Source | string,
    cache?: Cache,
    decompress?: DecompressFunc
  ) {
    super(source, cache, decompress);
    this.fallbackPMTiles = new PMTiles(fallbackSource);
  }
  coordinatesFromZXY(z: number, x: number, y: number) {
    // https://gis.stackexchange.com/a/461861
    const n = 2 ** z;
    return {
      latitude: (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI,
      longitude: (360 * x) / n - 180,
    };
  }
  async getZxy(
    z: number,
    x: number,
    y: number,
    signal?: AbortSignal
  ): Promise<RangeResponse | undefined> {
    const displayedTileNWCoordinates = this.coordinatesFromZXY(z, x, y);
    const displayedTileSECoordinates = this.coordinatesFromZXY(z, x + 1, y + 1);
    if (
      this.originalBounds &&
      (displayedTileNWCoordinates.longitude < this.originalBounds[0] ||
        displayedTileSECoordinates.longitude >= this.originalBounds[2] ||
        displayedTileSECoordinates.latitude <= this.originalBounds[1] ||
        displayedTileNWCoordinates.latitude > this.originalBounds[3])
    ) {
      const res = await this.fallbackPMTiles.getZxy(z, x, y, signal);
      if (res === undefined) {
        // pour éviter les tiles vides quand on zoom trop :
        // https://github.com/maplibre/maplibre-style-spec/issues/938
        throw new Error('Overzoomed tile');
      }
      return res;
    }
    return super.getZxy(z, x, y, signal);
  }
  async getHeader(): Promise<Header> {
    const h = await super.getHeader();
    if (!this.originalBounds) {
      this.originalBounds = [h.minLon, h.minLat, h.maxLon, h.maxLat];
      const fallbackPMTilesHeader = await this.fallbackPMTiles.getHeader();
      return {
        ...h,
        minLon: fallbackPMTilesHeader.minLon,
        maxLon: fallbackPMTilesHeader.maxLon,
        minLat: fallbackPMTilesHeader.minLat,
        maxLat: fallbackPMTilesHeader.maxLat,
      };
    }
    return h;
  }
}

export const buildPMTilesLayers = (
  source: string,
  theme: Flavor,
  options?: {
    lang?: string;
    labelsOnly?: boolean;
    // my custom options:
    buildings3D?: boolean;
    disableRoadShields?: boolean;
    regular?: string;
    bold?: string;
    italic?: string;
  }
): LayerSpecification[] => {
  const result = layers(
    source,
    {
      ...(theme === LIGHT
        ? {
            ...theme,
            water: '#68a7c4',
            highway_casing_early: '#d0e0d0',
            highway_casing_late: '#d0e0d0',
            highway: '#ffcc8a',
            major_casing_early: '#d0e0d0',
            major_casing_late: '#d0e0d0',
            major: '#ffeda8',
            earth: '#e1dfdb',
            buildings: '#beb9b1',
            park_b: '#bdc79c',
            wood_b: '#bdc79c',
            industrial: '#d1d1d0',
            scrub_b: '#b9d2ab',
          }
        : theme === DARK
          ? {
              ...theme,
              water: '#31354f',
              highway: '#675747',
              major: '#4d424d',
              earth: '#1c121c',
              buildings: '#000000',
              park_b: '#223022',
              wood_b: '#223022',
              industrial: '#193624',
              scrub_b: '#222323',
            }
          : theme),
      regular: options?.regular,
      bold: options?.bold,
      italic: options?.italic,
    },
    options
  );
  result.push({
    id: 'landuse_residential',
    type: 'fill',
    source: 'pmtiles',
    'source-layer': 'landuse',
    filter: ['in', 'kind', 'residential', 'dam'],
    paint: {
      'fill-opacity': 0.05,
    },
  });
  const buildings = result.find((layer) => layer.id === 'buildings') as FillLayerSpecification;
  if (buildings && options?.buildings3D) {
    const background = result.find(
      (layer) => layer.id === 'background'
    ) as BackgroundLayerSpecification;
    // rajout d'un détour autour des buildings
    result.push({
      id: `${buildings.id}-detour`,
      type: 'line',
      source: buildings.source,
      'source-layer': buildings['source-layer'],
      paint: {
        'line-color': (background?.paint || {
          'background-color': '#000000',
        })['background-color'],
        'line-width': 1,
      },
    });
    // ajout mode 3D
    result.push({
      id: `${buildings.id}-3d`,
      source: buildings.source,
      'source-layer': buildings['source-layer'],
      type: 'fill-extrusion',
      minzoom: 15,
      paint: {
        'fill-extrusion-color': [
          'interpolate',
          ['linear'],
          ['coalesce', ['get', 'height'], 0],
          0,
          buildings.paint!['fill-color'] as string,
          200,
          /*Color(buildings.paint!["fill-color"] as string)
            .darken(0.5)
            .hex(),*/
          buildings.paint!['fill-color'] as string, // chroma(customTheme.buildings).saturate(0.5).hex(),
          400,
          /*Color(buildings.paint!["fill-color"] as string)
            .darken(1)
            .hex(),*/
          buildings.paint!['fill-color'] as string, //chroma(customTheme.buildings).saturate(1).hex(),
        ],
        'fill-extrusion-height': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15,
          0,
          16,
          ['get', 'height'],
        ],
        'fill-extrusion-base': ['case', ['>=', ['get', 'zoom'], 16], ['get', 'min_height'], 0],
        'fill-extrusion-opacity': 0.6,
      },
    });
  }
  const roadShieldsIndex = result.findIndex((layer) => layer.id === 'roads_shields');
  if (roadShieldsIndex > -1 && options?.disableRoadShields) {
    result.splice(roadShieldsIndex, 1);
  }
  return result;
};
